import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/hooks/useAuth';
import { companiesApi, defaultFiscalStartMonthForCountry } from '@/lib/companies';
import { currencyForCountry } from '@/i18n/currencyForCountry';
import { CountrySelect, CurrencySelect, MultiCountrySelect } from '@/components/ui';

export function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: user, isLoading } = useCurrentUser();

  const initialCountry = (user?.countryCode || 'US').toUpperCase();
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState(initialCountry);
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(defaultFiscalStartMonthForCountry(initialCountry));
  const [reportingCurrency, setReportingCurrency] = useState(currencyForCountry(initialCountry));
  const [industry, setIndustry] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [targetRegions, setTargetRegions] = useState<string[]>([initialCountry === 'JP' ? 'JP' : 'US']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.countryCode) {
      setCountryCode(user.countryCode);
      setFiscalYearStartMonth(defaultFiscalStartMonthForCountry(user.countryCode));
      setReportingCurrency(currencyForCountry(user.countryCode));
      setTargetRegions([user.countryCode === 'JP' ? 'JP' : 'US']);
    }
  }, [user?.countryCode]);

  const primaryLocale = useMemo(() => (i18n.language || 'en').split('-')[0], [i18n.language]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-500 text-sm">
        {t('common.loading')}
      </div>
    );
  }
  if (!user) return <Navigate to="/?auth=required" replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('onboarding.name_required'));
      return;
    }
    setSubmitting(true);
    try {
      const { defaultWorkspaceId } = await companiesApi.create({
        name: name.trim(),
        countryCode,
        fiscalYearStartMonth,
        reportingCurrency,
        baseUnit: '1',
        primaryLocale,
        companyUrl: companyUrl.trim() || null,
        industry: industry.trim() || null,
        targetRegions,
      });
      navigate(`/app/workspace/${defaultWorkspaceId}/feed`);
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.data?.message || (err as Error).message;
      setError(typeof msg === 'string' ? msg : t('onboarding.create_failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <main className="mx-auto max-w-xl px-6 py-16">
        <div className="flex items-center gap-2.5 font-semibold text-ink-900 mb-8">
          <img src="/logo.png" alt="" width={28} height={28} className="rounded-md" />
          <span className="text-lg serif-jp">{t('brand.name')}</span>
        </div>

        <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">
          {t('onboarding.title')}
        </h1>
        <p className="mt-2 text-ink-600">{t('onboarding.subtitle')}</p>

        <form className="mt-8 space-y-5" onSubmit={submit}>
          <Field label={t('onboarding.company_name')} required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('onboarding.company_name_placeholder')}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30 outline-none"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t('onboarding.country')}>
              <CountrySelect
                value={countryCode}
                onChange={(v) => {
                  setCountryCode(v);
                  setFiscalYearStartMonth(defaultFiscalStartMonthForCountry(v));
                  setReportingCurrency(currencyForCountry(v));
                }}
              />
            </Field>

            <Field label={t('onboarding.reporting_currency')}>
              <CurrencySelect
                value={reportingCurrency}
                onChange={setReportingCurrency}
              />
            </Field>
          </div>

          <Field label={t('onboarding.industry')}>
            <input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder={t('onboarding.industry_placeholder')}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30 outline-none"
            />
          </Field>

          <Field label={t('onboarding.target_regions')} hint={t('onboarding.target_regions_hint')}>
            <MultiCountrySelect
              value={targetRegions}
              onChange={setTargetRegions}
              placeholder={t('onboarding.target_regions_placeholder', 'Select target countries…')}
            />
          </Field>

          <Field label={t('onboarding.company_url')} hint={t('onboarding.company_url_hint')}>
            <input
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              placeholder={t('onboarding.company_url_placeholder')}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30 outline-none"
            />
          </Field>

          {error && (
            <div className="rounded-lg bg-warn-500/10 border border-warn-500/30 text-warn-600 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition shadow-soft disabled:opacity-60"
          >
            {submitting ? t('onboarding.submitting') : t('onboarding.submit')}
          </button>
        </form>
      </main>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-700 mb-1.5">
        {label}
        {required && <span className="text-warn-500 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-ink-500 mt-1">{hint}</span>}
    </label>
  );
}
